import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { sapLogo, steadfastLogo } from "../lib/logos";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Clock, BookOpen, Award, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Welcome() {
  const { registerUser, user, loading } = useApp();
  const [name, setName] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("learner_name") || "";
    } catch {
      return "";
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  React.useEffect(() => {
    if (user) nav("/home");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = name.trim();
    if (clean.length < 2) {
      toast.error("Please enter your full name");
      return;
    }
    setSubmitting(true);
    try {
      await registerUser(clean);
      toast.success(`Welcome, ${clean}! Your name is now locked on the certificate.`);
      nav("/home");
    } catch (e) {
      toast.error("Could not save your name. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4"></div>
        <p className="text-muted-foreground text-sm">
          {user ? "Redirecting..." : "Loading module..."}
        </p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background paper-texture flex flex-col"
      data-testid="welcome-page"
    >
      {/* Top logos */}
      <div className="flex items-center justify-between px-6 sm:px-10 py-5">
        <img
          src={steadfastLogo}
          alt="Steadfast IT Consulting"
          className="h-14"
          data-testid="welcome-steadfast-logo"
        />
        <img src={sapLogo} alt="SAP" className="h-14" data-testid="welcome-sap-logo" />
      </div>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-3xl w-full text-center">
          <div className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.3em] text-accent mb-6">
            <span className="h-px w-10 bg-accent" />
            Professional Training Program
            <span className="h-px w-10 bg-accent" />
          </div>

          <h1 className="serif text-5xl sm:text-6xl lg:text-7xl leading-tight text-foreground">
            SAP: Basics &amp; Fundamentals
          </h1>

          <p className="mt-4 text-lg sm:text-xl text-muted-foreground">
            A 40-hour beginner-friendly e-learning journey on Understanding SAP —
            brought to you by <span className="font-semibold text-foreground">Steadfast IT Consulting</span>.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl mx-auto">
            {[
              { icon: Clock, label: "40 Hours" },
              { icon: BookOpen, label: "9 Modules + Capstone" },
              { icon: Award, label: "Certificate" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="rounded-md border border-border bg-card/70 px-4 py-3 flex items-center gap-2 justify-center"
              >
                <Icon className="w-4 h-4 text-primary" />
                <span className="mono text-xs sm:text-sm">{label}</span>
              </div>
            ))}
          </div>

          <form
            onSubmit={submit}
            className="mt-12 max-w-md mx-auto text-left"
            data-testid="welcome-form"
          >
            <Label htmlFor="name" className="serif text-xl text-foreground">
              Enter your full name to begin
            </Label>
            <p className="text-sm text-muted-foreground mt-1 mb-3 inline-flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              This name will be printed on your certificate and cannot be changed later.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Priya Kumar"
                className="flex-1 text-lg py-6"
                required
                autoFocus
                data-testid="welcome-name-input"
              />
              <Button
                type="submit"
                disabled={submitting}
                size="lg"
                className="sm:w-auto"
                data-testid="welcome-submit-btn"
              >
                {submitting ? "Starting…" : "Start Training"}
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              If there is ever a misspelling, contact{" "}
              <a
                href="mailto:operations@steadfastitconsulting.in"
                className="underline text-primary"
              >
                operations@steadfastitconsulting.in
              </a>{" "}
              before completion.
            </p>
          </form>
        </div>
      </main>

      <footer className="text-center pb-6">
        <p className="mono text-xs text-muted-foreground">
          © Steadfast IT Consulting • Training Content for Understanding SAP
        </p>
      </footer>
    </div>
  );
}
